import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import './JourneyModal.css';

// Animated waveform, shown above the lightbox's native audio controls.
// Ported from BT's MediaLightbox — same 15-bar layout, staggered
// durations/delays, and white/65%-opacity styling (the lightbox's
// near-black overlay is a close match for BT's own dark background, so
// no re-coloring was needed here, unlike the VisitModal tile). Unlike the
// tile's play/pause swap (which falls back to a mic icon), this always
// renders once the lightbox is open — animating while playing, freezing
// mid-shape via animationPlayState while paused. Purely decorative; bars
// don't correspond to actual audio levels.
const LIGHTBOX_WAVEFORM_BARS = [
    { h:  8, dur: 0.90, delay: 0.00 },
    { h: 14, dur: 0.75, delay: 0.10 },
    { h: 10, dur: 1.10, delay: 0.05 },
    { h: 20, dur: 0.80, delay: 0.15 },
    { h: 16, dur: 0.65, delay: 0.20 },
    { h: 26, dur: 0.90, delay: 0.08 },
    { h: 18, dur: 0.70, delay: 0.25 },
    { h: 30, dur: 1.00, delay: 0.12 },
    { h: 22, dur: 0.85, delay: 0.18 },
    { h: 16, dur: 0.65, delay: 0.22 },
    { h: 20, dur: 0.90, delay: 0.05 },
    { h: 12, dur: 1.10, delay: 0.28 },
    { h: 18, dur: 0.70, delay: 0.14 },
    { h: 10, dur: 0.80, delay: 0.08 },
    { h:  6, dur: 0.95, delay: 0.20 },
];

function LightboxWaveform({ isPlaying }) {
    // viewBox: 15 bars x 6px each (4 wide + 2 gap) + 2px left padding = 92.
    // Baseline at y=32; bars extend upward from there.
    return (
        <svg width="120" height="64" viewBox="0 0 92 40" fill="none" aria-hidden="true">
            {LIGHTBOX_WAVEFORM_BARS.map((bar, i) => (
                <rect
                    key={i}
                    x={2 + i * 6}
                    y={32 - bar.h}
                    width={4}
                    height={bar.h}
                    rx={2}
                    fill="white"
                    fillOpacity={0.65}
                    style={{
                        transformOrigin: `${2 + i * 6 + 2}px 32px`,
                        animationName: 'jdLightboxWaveformPulse',
                        animationDuration: `${bar.dur}s`,
                        animationDelay: `${bar.delay}s`,
                        animationTimingFunction: 'ease-in-out',
                        animationIterationCount: 'infinite',
                        animationPlayState: isPlaying ? 'running' : 'paused',
                    }}
                />
            ))}
        </svg>
    );
}

const JourneyModal = ({ isOpen, onClose, visits, onSignInClick }) => {
    const [lightboxItem, setLightboxItem] = useState(null); // { url, type }
    // Text-only expand/collapse (2026-08-09 design decision): scoped to
    // just the text block, not the whole visit — media always renders in
    // full regardless. Each visit's text expands independently of every
    // other visit's, hence a Set rather than a single "currently expanded"
    // value.
    const [expandedTexts, setExpandedTexts] = useState(new Set());
    const [videoBoxSize, setVideoBoxSize] = useState(null); // { width, height } in px — computed once real video dimensions are known, since relying on CSS max-width/max-height alone doesn't reliably size portrait video correctly in Safari's native video chrome
    const [isLightboxAudioPlaying, setIsLightboxAudioPlaying] = useState(false); // drives the waveform's running/paused animation state below
    const [userGroups, setUserGroups] = useState({ 
        isFamily: false, 
        isAdmin: false,
        isAuthenticated: false 
    });
    
    useEffect(() => {
        const loadUserGroups = async () => {
            try {
                const session = await fetchAuthSession();
                const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
                setUserGroups({
                    isFamily: groups.includes('family'),
                    isAdmin: groups.includes('admin'),
                    isAuthenticated: !!session.tokens
                });
            } catch (err) {
                setUserGroups({ 
                    isFamily: false, 
                    isAdmin: false,
                    isAuthenticated: false 
                });
            }
        };
        
        if (isOpen) {
            loadUserGroups();
        }
    }, [isOpen]);
    
    // iOS Safari treats <audio autoplay> more strictly than <video
    // autoplay> — the HTML attribute alone often silently fails for
    // audio specifically. Explicitly calling .play() here, right as the
    // lightbox opens, is the reliable fix. Moved above the early return
    // below — hooks must be called unconditionally on every render, and
    // this was previously placed after `if (!isOpen) return null`, which
    // broke that rule.
    const lightboxAudioRef = useRef(null);
    useEffect(() => {
        setIsLightboxAudioPlaying(false);
        if (lightboxItem?.type === 'audio' && lightboxAudioRef.current) {
            lightboxAudioRef.current.play().catch(() => {});
        }
    }, [lightboxItem]);

    if (!isOpen) return null;

    const toggleTextExpand = (visitId) => {
        setExpandedTexts(prev => {
            const next = new Set(prev);
            if (next.has(visitId)) {
                next.delete(visitId);
            } else {
                next.add(visitId);
            }
            return next;
        });
    };

    // Check if visit has any family-only content
    const hasFamilyContent = (visit) => {
        return visit.media.some(m => m.visibility === 'family');
    };

    // Check if user can see this specific visit's content
    const canViewVisitContent = (visit) => {
        const hasFamily = hasFamilyContent(visit);
        
        // If visit has any family content and user is NOT family/admin, hide ALL content
        if (hasFamily && !userGroups.isFamily && !userGroups.isAdmin) {
            return false;
        }
        
        return true;
    };

    // Filter media - only for visits where user can see content.
    // Visual items (photo/video/audio) are combined into ONE array, in
    // original upload order, so the journey grid renders chronologically
    // and audio isn't silently dropped. Text stays separate — it renders
    // below the grid, not as a tile within it.
    const getVisibleMedia = (visit) => {
        if (!canViewVisitContent(visit)) {
            return { visualItems: [], texts: [] };
        }

        const items = Object.values(visit.media || {})
            .sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt));
        return {
            visualItems: items.filter(m => m.type === 'photo' || m.type === 'video' || m.type === 'audio'),
            texts: items.filter(m => m.type === 'text')
        };
    };

    const openLightbox = (url, type) => {
        setVideoBoxSize(null);
        setLightboxItem({ url, type });
    };

    // Computes the actual pixel box the video should render at, based on
    // its real dimensions once known — rather than relying on CSS
    // max-width/max-height auto-sizing, which Safari doesn't always apply
    // correctly for portrait video with native controls.
    const handleLightboxVideoLoaded = (e) => {
        const { videoWidth, videoHeight } = e.target;
        console.log('Lightbox video loaded:', { videoWidth, videoHeight, innerWidth: window.innerWidth, innerHeight: window.innerHeight });
        if (!videoWidth || !videoHeight) return;

        const maxWidth = window.innerWidth * 0.9;
        const maxHeight = window.innerHeight * 0.9;
        const naturalRatio = videoWidth / videoHeight;
        const maxRatio = maxWidth / maxHeight;

        if (naturalRatio > maxRatio) {
            setVideoBoxSize({ width: maxWidth, height: maxWidth / naturalRatio });
        } else {
            setVideoBoxSize({ height: maxHeight, width: maxHeight * naturalRatio });
        }
    };

    const closeLightbox = () => {
        setLightboxItem(null);
    };

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Disc Journey</h2>
                        <button className="close-button" onClick={onClose}>&times;</button>
                    </div>
                    <div className="journey-timeline">
                        {visits.map((visit, index) => {
                            const { visualItems, texts } = getVisibleMedia(visit);
                            const isFamilyOnly = hasFamilyContent(visit);
                            const isTextExpanded = expandedTexts.has(visit.visitId);
                            const canView = canViewVisitContent(visit);

                            return (
                                <div key={visit.visitId} className="journey-stop">
                                    <div className="journey-number">{index + 1}</div>
                                    <div className="journey-details">
                                        <h3>{visit.courseName}</h3>
                                        <p className="journey-date">
                                            {new Date(visit.timestamp).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                            {(visit.location.city || visit.location.state || visit.location.country) && (
                                                <> &middot; {[visit.location.city, visit.location.state, visit.location.country].filter(Boolean).join(', ')}</>
                                            )}
                                        </p>
                                        
                                        {/* Family-Only Badge */}
                                        {isFamilyOnly && (
                                            <div className={`family-only-badge ${userGroups.isFamily || userGroups.isAdmin ? 'family-view' : ''}`}>
                                                {userGroups.isFamily || userGroups.isAdmin 
                                                    ? "Shared with Jeffrey's Family"
                                                    : "Shared with Jeffrey's Family Only"}
                                            </div>
                                        )}
                                        
                                        {/* Sign-In Prompt (only for family-only content when not authenticated) */}
                                        {isFamilyOnly && !userGroups.isAuthenticated && (
                                            <button 
                                                className="family-signin-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSignInClick();
                                                }}
                                            >
                                                I'm Family - Sign Me In
                                            </button>
                                        )}
                                        
                                        {/* Show content only if user has permission */}
                                        {canView && (
                                            <>
                                                {/* Combined Photo/Video/Audio grid, in original upload
                                                    order — replaces the old separate photos/videos
                                                    grids, which grouped by type instead of chronology
                                                    and had no audio rendering at all. */}
                                                {visualItems.length > 0 && (
                                                    <div className="media-thumbnails">
                                                        {visualItems.map((item, i) => {
                                                            if (item.type === 'photo') {
                                                                return (
                                                                    <img
                                                                        key={i}
                                                                        src={item.url}
                                                                        alt="Visit"
                                                                        className="journey-thumbnail"
                                                                        onClick={() => openLightbox(item.url, 'photo')}
                                                                    />
                                                                );
                                                            }
                                                            if (item.type === 'video') {
                                                                return (
                                                                    <div
                                                                        key={i}
                                                                        className="journey-video-thumbnail"
                                                                        onClick={() => openLightbox(item.url, 'video')}
                                                                    >
                                                                        <video poster={item.thumbnailUrl || undefined}>
                                                                            <source src={item.url} type="video/mp4" />
                                                                        </video>
                                                                        <div className="video-play-overlay">▶</div>
                                                                    </div>
                                                                );
                                                            }
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className="journey-audio-thumbnail"
                                                                    onClick={() => openLightbox(item.url, 'audio')}
                                                                >
                                                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                                                        <line x1="12" y1="19" x2="12" y2="23" />
                                                                        <line x1="8" y1="23" x2="16" y2="23" />
                                                                    </svg><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                
                                                {/* Text Content — 150-char preview + Show More/Less,
                                                    scoped to text only (design decision, 2026-08-09).
                                                    Media above is unaffected — always renders in full. */}
                                                {texts.length > 0 && (
                                                    <div className="journey-texts">
                                                        {texts.map((text, i) => (
                                                            <p key={i} className="journey-text">
                                                                {isTextExpanded ? text.content : (
                                                                    text.content.length > 150
                                                                        ? text.content.substring(0, 150) + '...'
                                                                        : text.content
                                                                )}
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}

                                                {texts.some(text => text.content.length > 150) && (
                                                    <button
                                                        className="expand-button"
                                                        onClick={() => toggleTextExpand(visit.visitId)}
                                                    >
                                                        {isTextExpanded ? 'Show Less' : 'Show More'}
                                                    </button>
                                                )}
                                                
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Lightbox for full-size images/videos/audio */}
            {lightboxItem && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
                    {lightboxItem.type === 'video' ? (
                        <video 
                            className="lightbox-content" 
                            controls 
                            autoPlay
                            playsInline
                            onLoadedMetadata={handleLightboxVideoLoaded}
                            style={videoBoxSize ? { width: `${videoBoxSize.width}px`, height: `${videoBoxSize.height}px` } : undefined}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <source src={lightboxItem.url} type="video/mp4" />
                        </video>
                    ) : lightboxItem.type === 'audio' ? (
                        <div className="lightbox-audio-wrapper" onClick={(e) => e.stopPropagation()}>
                            <LightboxWaveform isPlaying={isLightboxAudioPlaying} />
                            <audio
                                ref={lightboxAudioRef}
                                className="lightbox-audio"
                                src={lightboxItem.url}
                                controls
                                autoPlay
                                onPlay={() => setIsLightboxAudioPlaying(true)}
                                onPause={() => setIsLightboxAudioPlaying(false)}
                                onEnded={() => setIsLightboxAudioPlaying(false)}
                            />
                        </div>
                    ) : (
                        <img 
                            className="lightbox-content" 
                            src={lightboxItem.url} 
                            alt="Full size" 
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default JourneyModal;